import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { spinGacha } from '../../services/api';
import { useAuth } from '../auth';
import { useCheckout } from '../checkout';
import { useLocker } from '../locker';
import { useToast } from '../toast';
import { logGachaSpin } from '../../services/analytics';

const GachaContext = createContext({
  isSpinning: false,
  currentPrize: null,
  prizeModalOrigin: null,
  isPrizeModalOpen: false,
  latestMachineState: null,
  startGacha: async () => {},
  completeAnimation: async () => {},
  tryAgain: () => {},
  closePrizeModal: () => {},
  viewLockerFromPrize: () => {},
  shipLockerFromPrize: () => {},
  expandLockerFromPrize: () => {},
  clearPrize: () => {},
  setPrizeModalOrigin: () => {},
  setIsPrizeModalOpen: () => {},
});

const GachaProvider = ({ children }) => {
  const { user, login } = useAuth();
  const { revertTokenSpend, queueTokenGain } = useCheckout();
  const { setLockerGainEvent, setLockerDecision } = useLocker();
  const { showToast } = useToast();
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentPrize, setCurrentPrize] = useState(null);
  const [prizeModalOrigin, setPrizeModalOrigin] = useState(null);
  const [isPrizeModalOpen, setIsPrizeModalOpen] = useState(false);
  const [latestMachineState, setLatestMachineState] = useState(null);

  const resolveLockerItems = useCallback((account) => {
    if (!account) {
      return [];
    }
    if (Array.isArray(account.locker)) {
      return account.locker;
    }
    if (Array.isArray(account.history)) {
      return account.history;
    }
    return [];
  }, []);

  const startGacha = useCallback(
    async ({ machineId, tokenCost } = {}) => {
      if (!user) {
        showToast({ message: 'Please sign in to spin.', tone: 'error' });
        return;
      }

      const currentTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
      if (user && typeof tokenCost === 'number' && currentTokens < tokenCost) {
        showToast({ message: 'Not enough tokens to spin!', tone: 'error' });
        return;
      }

      // Only check pendingPrize - backend will handle locker capacity check
      // Users can spin even if locker has 8 items, but will get pendingPrize on 9th item
      if (user?.pendingPrize) {
        const lockerItems = resolveLockerItems(user);
        setLockerDecision({
          pendingPrize: user.pendingPrize,
          lockerItems,
          maxSlots: Number.isFinite(user?.maxLockerSlots) ? user.maxLockerSlots : 8,
        });
        // CHANGED: Removed toast - LockerFullModal will be shown instead
        return;
      }

      setIsSpinning(true);
      // Token animation handled when user inserts tokens; avoid double events here
    },
    [user, showToast, setLockerDecision, resolveLockerItems],
  );

  const completeAnimation = useCallback(
    async ({ machineId, tokenCost } = {}) => {
      if (!machineId) {
        showToast({ message: 'Please select a machine before spinning.', tone: 'error' });
        setIsSpinning(false);
        revertTokenSpend(tokenCost || 0);
        return;
      }

      try {
        const result = await spinGacha({ userId: user?.id, machineId, tokenCost });
        // CHANGED: Log analytics event for gacha spin
        logGachaSpin(machineId, tokenCost);
        setCurrentPrize(result.prize);
        if (result.machine) {
          setLatestMachineState(result.machine);
        }
        if (result.user) {
          const previousTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
          const nextTokens =
            typeof result.user?.tokens === 'number' ? result.user.tokens : previousTokens;
          // For normal spins, show net token gain; for welcome/token-bonus spins, show the prize amount instead
          const tokenBonus =
            typeof result.prize?.tokenBonus === 'number' ? result.prize.tokenBonus : null;
          if (Number.isFinite(tokenBonus) && tokenBonus > 1) {
            queueTokenGain(tokenBonus);
          } else if (nextTokens > previousTokens) {
            queueTokenGain(nextTokens - previousTokens);
          }
          const previousLockerCount = resolveLockerItems(user).length;
          const nextLockerCount = resolveLockerItems(result.user).length;
          if (nextLockerCount > previousLockerCount) {
            setLockerGainEvent({
              amount: nextLockerCount - previousLockerCount,
              id: Date.now(),
            });
          }
          // CHANGED: Update user state directly from API response instead of calling refreshUser
          // This prevents an unnecessary /auth/session API call (spinGacha already returns updated user)
          if (result.user && typeof login === 'function') {
            login(result.user);
            // CHANGED: The spin cost is now reflected in result.user.tokens.
            // Clear the optimistic spend so header token count matches the server balance.
            revertTokenSpend(tokenCost || 0);
          }
          if (result.user?.pendingPrize) {
            setLockerDecision({
              pendingPrize: result.user.pendingPrize,
              lockerItems: resolveLockerItems(result.user),
              maxSlots: Number.isFinite(result.user?.maxLockerSlots)
                ? result.user.maxLockerSlots
                : 8,
              machineName: result.machine?.name || latestMachineState?.name || null,
            });
            // Don't auto-open modal - wait for user to click capsule
          } else {
            setLockerDecision(null);
            // Don't auto-open modal - wait for user to click capsule
          }
        }
      } catch (err) {
        console.error('Failed to spin PonPon!:', err);
        showToast({
          message: err.response?.data?.error || 'Failed to spin PonPon!',
          tone: 'error',
        });
        revertTokenSpend(tokenCost || 0);
      } finally {
        setIsSpinning(false);
      }
    },
    [
      user,
      showToast,
      revertTokenSpend,
      queueTokenGain,
      setLockerGainEvent,
      setLockerDecision,
      latestMachineState,
      resolveLockerItems,
      login,
    ],
  );

  const tryAgain = useCallback(
    ({ machineId, tokenCost } = {}) => {
      const currentTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
      if (user && typeof tokenCost === 'number' && currentTokens < tokenCost) {
        showToast({ message: 'Not enough tokens to spin again!', tone: 'error' });
        return;
      }
      setCurrentPrize(null);
      startGacha({ machineId, tokenCost });
    },
    [user, showToast, startGacha],
  );

  const closePrizeModal = useCallback(() => {
    setIsPrizeModalOpen(false);
    setPrizeModalOrigin(null);
    // CHANGED: Delay clearing prize to allow reveal animation to complete (650ms timeout in Homepage)
    setTimeout(() => {
      setCurrentPrize(null);
    }, 700);
  }, []);

  const viewLockerFromPrize = useCallback(() => {
    setIsPrizeModalOpen(false);
    setPrizeModalOrigin(null);
    // CHANGED: Delay clearing prize to allow reveal animation to complete (650ms timeout in Homepage)
    setTimeout(() => {
      setCurrentPrize(null);
    }, 700);
  }, []);

  const shipLockerFromPrize = useCallback(() => {
    setIsPrizeModalOpen(false);
    setPrizeModalOrigin(null);
    // CHANGED: Delay clearing prize to allow reveal animation to complete (650ms timeout in Homepage)
    setTimeout(() => {
      setCurrentPrize(null);
    }, 700);
  }, []);

  const expandLockerFromPrize = useCallback(() => {
    setIsPrizeModalOpen(false);
    setPrizeModalOrigin(null);
    // CHANGED: Delay clearing prize to allow reveal animation to complete (650ms timeout in Homepage)
    setTimeout(() => {
      setCurrentPrize(null);
    }, 700);
  }, []);

  const clearPrize = useCallback(() => {
    setCurrentPrize(null);
    setIsPrizeModalOpen(false);
    setPrizeModalOrigin(null);
  }, []);

  React.useEffect(() => {
    if (!isSpinning && !currentPrize && !user?.pendingPrize) {
      setIsPrizeModalOpen(false);
      setPrizeModalOrigin(null);
    }
  }, [isSpinning, currentPrize, user?.pendingPrize]);

  const value = useMemo(
    () => ({
      isSpinning,
      currentPrize,
      prizeModalOrigin,
      isPrizeModalOpen,
      latestMachineState,
      startGacha,
      completeAnimation,
      tryAgain,
      closePrizeModal,
      viewLockerFromPrize,
      shipLockerFromPrize,
      expandLockerFromPrize,
      clearPrize,
      setPrizeModalOrigin,
      setIsPrizeModalOpen,
    }),
    [
      isSpinning,
      currentPrize,
      prizeModalOrigin,
      isPrizeModalOpen,
      latestMachineState,
      startGacha,
      completeAnimation,
      tryAgain,
      closePrizeModal,
      viewLockerFromPrize,
      shipLockerFromPrize,
      expandLockerFromPrize,
      clearPrize,
      setPrizeModalOrigin,
      setIsPrizeModalOpen,
    ],
  );

  return <GachaContext.Provider value={value}>{children}</GachaContext.Provider>;
};

export const useGacha = () => {
  return useContext(GachaContext);
};

export default GachaProvider;
